import {Context, Middleware} from '@nuxt/types';
import extendQueryParams from "~/modules/deeplink/helper/deeplinkQueryParams";
import {useCustomerStore} from "~/modules/customer/stores/customer";
import jwtDecode from "jwt-decode";
import {Logger} from "~/helpers/logger";

type DecodedCustomerToken = {
  exp: number | null;
};

const redirectToLogin = (context: Context) => {
  const loginPage = context.localeRoute({name: 'login'});
  const path = extendQueryParams(loginPage.path, context.route);

  context.redirect(path);
};

const invalidateSession = (context: Context) => {
  const customerStore = useCustomerStore();

  customerStore.setIsLoggedIn(false);
  customerStore.user = null;
  context.app.$vsf.$magento.config.state.removeCustomerToken();
  context.app.$vsf.$magento.config.state.removeCartId();
};

// Lightweight GraphQL probe — only requires a valid auth token, returns nothing significant
const AUTH_PROBE_QUERY = `query { customer { firstname } }`;

const probeTokenValidity = async (context: Context): Promise<boolean> => {
  try {
    const { data } = await context.app.$vsf.$magento.api.customQuery({
      query: AUTH_PROBE_QUERY,
    });
    // If we got any data back (even null customer), the token was accepted
    return data !== undefined;
  } catch (error: any) {
    const msg = (error?.message || '').toLowerCase();
    const networkMsg = (error?.networkError?.message || '').toLowerCase();
    const isAuthFailure =
      error?.response?.status === 401 ||
      error?.networkError?.statusCode === 401 ||
      msg.includes('401') ||
      msg.includes('composite reader') ||
      msg.includes('not authorized') ||
      msg.includes('unauthorized') ||
      networkMsg.includes('401');

    if (isAuthFailure) {
      Logger.debug('middleware/is-authenticated: backend rejected token, treating as expired');
      return false;
    }
    // Non-auth errors (network timeout, etc.) — don't force logout
    Logger.warn('middleware/is-authenticated: probe query failed with non-auth error', error?.message);
    return true;
  }
};

const middleware: Middleware = async (context) => {
  const customerToken = context.app.$vsf.$magento.config.state.getCustomerToken();

  if (!customerToken) {
    invalidateSession(context);
    redirectToLogin(context);
    return;
  }

  try {
    const nowDate = new Date();
    nowDate.setSeconds(nowDate.getSeconds() + 10);
    const now = nowDate.getTime();

    const decodedToken = jwtDecode<DecodedCustomerToken>(customerToken);
    const tokenExpirationInMs = decodedToken.exp ? decodedToken.exp * 1000 : null;

    if (tokenExpirationInMs === null || tokenExpirationInMs < now) {
      invalidateSession(context);
      redirectToLogin(context);
      return;
    }
  } catch (exception) {
    Logger.error('middleware/is-authenticated', exception);
    invalidateSession(context);
    redirectToLogin(context);
    return;
  }

  // JWT looks locally valid — probe the backend to verify the signature is accepted
  const isValid = await probeTokenValidity(context);
  if (!isValid) {
    invalidateSession(context);
    redirectToLogin(context);
  }
};
export default middleware;
